import type { ThemeColors } from "./theme.types"

export class GrayTheme implements ThemeColors {
  readonly name = "gray" as const

  readonly bgCanvas = "#121212"
  readonly bgSurface = "#262626"
  readonly textPrimary = "#FFFFFF"
  readonly textSecondary = "#A0A0A0"
  readonly borderDefault = "#4D4D4D"
  readonly accentPrimary = "#FFFFFF"
  readonly accentPrimaryActive = "#bababa"
  readonly accentFav = "#111827"
  readonly textOnPrimary = "#000000"
  readonly neutralLine = "#3F3F3F"
  readonly shadowSm = "rgba(255, 255, 255, 0.15)"
  readonly shadowLg = "rgba(255, 255, 255, 0.25)"
  readonly iconFilterMono = "brightness(0) invert(1)"

  readonly iconOverlayColor = "#E0E0E0"
  readonly iconOverlayOpacity = 1     // 100%

  readonly button = {
    primaryBg: "#FFFFFF",
    primaryHover: "#9A9A9A",
    primaryActive: "#8A8A8A",
    primaryShadowHover: "rgba(138, 138, 138, 0.3)",
    textOnPrimary: "#000000",
    secondaryBg: "#333333",
    textOnSecondary: "#FFFFFF",
    disabledBg: "#262626",
    disabledText: "#525252",
    disabledBorder: "#323232",
    successBg: "#FFFFFF",
    successText: "#000000",
    successHover: "#808080",
    successPressed: "#7C7C7C",
  }

  readonly folder = {
    filledBg: "#5B8CFF",
    textPrimary: "#FFFFFF",
    textSub: "#FFFFFF",
  }

  readonly destructive = {
    text: "#E0E0E0",
    textHighlight: "#E0E0E0",
    hover: "#1F1F1F",
    active: "#262626",
    card: "#1E1E1E",
  }

  readonly presence = {
    active: "#111827",
    away: "#111827",
  }

  readonly codeEditor = {
    bg: "#121212",
    gutterBg: "#101010",
    lineNumber: "#4D4D4D",
    activeLine: "#1A1A1A",
    selection: "#474747",
  }

  readonly tooltip = {
    bgMain: "#191919",
    textLabel: "#F9F9F9",
    bgShortcut: "#323232",
    textShortcut: "#A0A0A0",
    bgCode: "#101010",
    textGutter: "#535353",
    borderCode: "#313131",
    codeCmd: "#9A9A9A",
    codeBraces: "#F9F9F9",
    codeParam: "#E7E7E7",
    codeKeyword: "#9A9A9A",
    codeString: "#EBEBEB",
    codeParameter: "#B7B7B7",
    codeArgument: "#BCBCBC",
    codeComment: "#929292",
    codeMath: "#BEBEBE",
  }

  readonly input = {
    bg: "#121212",
    border: "#313131",
    borderFocus: "#9A9A9A",
    borderError: "#999999",
    error: "#999999",
    errorBg: "#1F1F1F",
    errorIcon: "#999999",
    successText: "#D8D8D8",
    success: "#9C9C9C",
    successBg: "#232323",
    warning: "#BFBFBF",
    warningText: "#222222",
    warningBg: "#E2E2E2",
    textTyped: "#F9F9F9",
    textPlaceholder: "#535353",
    iconResting: "#A0A0A0",
    iconActive: "#9A9A9A",
  }

  readonly social = {
    bg: "#1E1E1E",
    border: "#313131",
    hover: "#313131",
    dividerSubtle: "#3F3F3F",
    icon: "#FFFFFF",
  }

  readonly nav = {
    bgActive: "#323232",
    textActive: "#FFFFFF",
    textRest: "#A0A0A0",
    hover: "#464646",
  }

  readonly userCursors = [
    "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D",
    "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D",
    "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D",
    "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D", "#4D4D4D",
  ]

  // ── Derived — see file header note ──
  readonly editorPanels = {
    header: {
      border: "#4D4D4D",
      backBg: "#121212",
      backBorder: "#4D4D4D",
      backText: "#D8D8D8",
      backHover: "#4D4D4D",
      compileBg: "#333333",
      compileHover: "#464646",
      compileActive: "#4D4D4D",
      pathText: "#A0A0A0",
      titleText: "#FFFFFF",
    },
    icon: {
      filter: "invert(1)",
    },
    ribbon: {
      bg: "#1E1E1E",
      border: "#4D4D4D",
      text: "#A0A0A0",
      divider: "#4D4D4D",
      tabHover: "#464646",
      tabActiveBg: "#333333",
      tabActiveText: "#FFFFFF",
      fieldBorder: "#4D4D4D",
      fieldBg: "#333333",
      fieldHover: "#464646",
      fieldText: "#D8D8D8",
      iconHover: "#4D4D4D",
      groupActiveBg: "#464646",
      popupBg: "#1E1E1E",
      groupBg: "#333333",
      groupDivider: "#4D4D4D",
    },
    explorer: {
      canvasBg: "#101010",
      sidebarBorder: "#4D4D4D",
      panelBorder: "#4D4D4D",
      panelShadow: "none",
      iconHoverBg: "#464646",
      selectedBg: "rgba(77, 77, 77, 0.5)",
      layersIconBg: "#262626",
      layersIconBorder: "0.5px solid #4D4D4D",
      layoutCanvasBg: "#121212",
    },
    explorerFrame: {
      background: "#262626",
      border: "#4D4D4D",
      hoverBg: "#464646",
      selectedBg: "#4D4D4D",
      textPrimary: "#D8D8D8",
      textMuted: "#A0A0A0",
      accent: "#5B8CFF",
      shadow: "#000000",
      danger: "#EF4444",
      folderIcon: "#FFFFFF",
      fileIcon: "#FFFFFF",
      chevronIcon: "#FFFFFF",
      treeLine: "#4D4D4D",
    },
    dock: {
      btnBorder: "#4D4D4D",
    },
    codeEditorPanel: {
      breadcrumbBg: "#323232",
      breadcrumbBorder: "#4D4D4D",
      breadcrumbText: "#A0A0A0",
      breadcrumbDivider: "#3F3F3F",
      breadcrumbMutedText: "#A0A0A0",
    },
    console: {
      innerBg: "#121212",
      tabActiveBorder: "#4D4D4D",
      tabActiveBorderWidth: "0.5px",
      rowText: "#A0A0A0",
      rowBorder: "#3F3F3F",
      tabActiveText: "#FFFFFF",
      tabText: "#A0A0A0",
      tabHoverText: "#FFFFFF",
      emptyText: "#A0A0A0",
      codeBg: "rgba(0,0,0,0.20)",
      tabActiveBg: "#4D4D4D",
      warnRowBg: "#E2E2E2",
    },
    status: {
      errorBg: "#1F1F1F",
      errorBorder: "#999999",
      errorText: "#E0E0E0",
      errorBadgeBg: "#262626",
      errorBadgeText: "#E0E0E0",
      warnBg: "#E2E2E2",
      warnBorder: "#BFBFBF",
      warnText: "#222222",
      warnBadgeBg: "#E2E2E2",
      warnBadgeText: "#222222",
      successBg: "#232323",
      successBorder: "#9C9C9C",
      successText: "#D8D8D8",
      successBadgeBg: "#232323",
      successBadgeText: "#D8D8D8",
      neutralBadgeBg: "#4D4D4D",
      linkText: "#FFFFFF",
      linkHover: "#A0A0A0",
      errorTextStrong: "#E0E0E0",
      errorDismissHover: "#E0E0E0",
    },
    preview: {
      canvasBg: "#262626",
      wrapperBg: "#262626",
      bgAlt: "#121212",
      tabBarBg: "#323232",
      tabBorder: "#4D4D4D",
      contentBg: "#262626",
      text: "#A0A0A0",
      mutedText: "#A0A0A0",
      hintIcon: "#A0A0A0",
      errorText: "#E0E0E0",
      pageAreaBg: "#121212",
      imgBg: "#101010",
      imgBorder: "#4D4D4D",
    },
    pdf: {
      errorBg: "#1F1F1F",
      errorBorder: "#999999",
      errorText: "#E0E0E0",
    },
    toggle: {
      onBg: "#FFFFFF",
      trackBg: "#333333",
      trackBorder: "#4D4D4D",
      knobBg: "#4D4D4D",
      knobBorderOn: "#FFFFFF",
      knobBorderOff: "#A0A0A0",
      labelOn: "#FFFFFF",
      labelOff: "#A0A0A0",
    },
    dropdown: {
      triggerBg: "#333333",
      triggerBorder: "#4D4D4D",
    },
    versionHistory: {
      headerBorder: "#4D4D4D",
      titleText: "#FFFFFF",
      closeText: "#A0A0A0",
      closeHoverBg: "#464646",
      subtext: "#A0A0A0",
      groupLabelBorder: "#3F3F3F",
      groupLabelText: "#A0A0A0",
      emptyText: "#A0A0A0",
      itemHoverBg: "#464646",
      itemSelectedBorder: "#4D4D4D",
      itemSelectedBg: "#4D4D4D",
      itemTimeText: "#A0A0A0",
      itemNameText: "#A0A0A0",
      itemDescText: "#E0E0E0",
      itemHashText: "#A0A0A0",
      iconAccent: "#FFFFFF",
      footerBorder: "#3F3F3F",
      footerBg: "#121212",
      footerLabel: "#A0A0A0",
      codeBorder: "#4D4D4D",
      codeBg: "#121212",
      codeText: "#E0E0E0",
      offlineBg: "#E2E2E2",
      offlineBorder: "#BFBFBF",
      offlineText: "#222222",
      scrollbarThumb: "#4D4D4D",
      panelShadow: "-2px 0 12px rgba(255,255,255,0.25)",
      loadingBorder: "#3F3F3F",
    },
    cookie: {
      border: "#4D4D4D",
      titleText: "#FFFFFF",
      bodyText: "#A0A0A0",
      btnBorder: "#4D4D4D",
      btnHover: "#464646",
    },
    tabs: {
      activeBg: "#121212",
      activeText: "#FFFFFF",
      inactiveText: "#A0A0A0",
      inactiveHoverText: "#FFFFFF",
      inactiveHoverBg: "#464646",
      barBg: "#323232",
      barBorder: "#4D4D4D",
      detachedBadgeBg: "#4D4D4D",
    },
    browserTab: {
      text: "#FFFFFF",
      btnBorder: "#4D4D4D",
      btnBorderWidth: "0.2px",
      btnBgPdf: "#121212",
      btnBgFile: "#121212",
    },
    divider: {
      bg: "#121212",
    },
  }

  // ── Derived — see file header note ──
  readonly dashboardPanels = {
    topbar: {
      text: "#FFFFFF",
      notifText: "#FFFFFF",
      notifTextStrong: "#FFFFFF",
      notifBadgeBlue: "rgba(160, 160, 160, 0.15)",
      notifBadgeRed: "rgba(224, 224, 224, 0.20)",
      notifBadgeOrange: "rgba(160, 160, 160, 0.25)",
      notifBadgeGreen: "rgba(77, 77, 77, 0.4)",
    },
    surfaceHover: "#464646",
    surfaceHoverMuted: "#464646",
    accentStrong: "#FFFFFF",
    accentHover: "#9A9A9A",
    strongText: "#FFFFFF",
    filenameText: "#FFFFFF",
    mutedTextAlt: "#A0A0A0",
    textPrimaryAlt: "#FFFFFF",
    textSecondary: "#9c9c9c",
    textMutedAlt: "#A0A0A0",
    cardSelectedBg: "rgba(160, 160, 160, 0.15)",
    chipBg: "#121212",
    chipText: "#FFFFFF",
    dialogText: "#FFFFFF",
    dialogTextFaint: "#525252",
    tagBg: "#4D4D4D",
    warnBg: "#E2E2E2",
    warnBorder: "#BFBFBF",
    dangerBg: "#1F1F1F",
    dangerBorder: "#999999",
    iconMutedFilter: "brightness(0) invert(1) opacity(0.65)",
    highlightBg: "#4D4D4D",
    highlightText: "#FFFFFF",
  }
}

export const grayTheme = new GrayTheme()
